<script setup>
import {ref, onMounted, inject, computed} from "vue";

const showMessage = inject('showMessage');
const pingRunner = ref();
const userStatus = {
  0: 'logged_out',
  1: 'logged_in',
  2: 'pending'
};

const user = ref({
  status: userStatus["0"],
  data: {},
  tokens:{
    access_token:false,
    refresh_token:false
  }
});

const getBearer = () => {
  let bearerToken = `Bearer ${user.value.tokens.access_token}`;
  return bearerToken;
};

const userIndicator = computed(()=>({
  'private': user.value.status === userStatus["0"],
  'private-loggedin': user.value.status === userStatus["1"],
  'pending': user.value.status === userStatus["2"],
}));

const userLoggedIn = computed(()=>{
  return user.value.status === userStatus["1"];
});

const openLogin = (callback = null) => {
  if(callback == null){
    callback = location.pathname+window.location.hash;
  }
  getLoginUri(callback).then(url => {
      location.href = url;
    }).catch(error => {
      console.error(error.message);
    }).finally(() => {
      // loadingState.value = false;
    });
};

const getUserData = async () => {
  const user_data = await fetch('/current_user', {
    method: "GET",
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': getBearer()
    },
  });
  if(!user_data.ok){
    throw new Error(`${user_data.statusText} (${user_data.status})`);
  }

  return await user_data.json()
}

const getUserStatus = async () =>{
  const user_status = await fetch('/user_status', {
    method: "GET",
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
  });
  if(!user_status.ok){
    throw new Error(`${user_status.statusText} (${user_status.status})`);
  }

  return await user_status.json()
}

const userHasRole = (role) =>{
  if(user.value.data.hasOwnProperty('realm_access') && user.value.data.realm_access.hasOwnProperty('roles')){
    return user.value.data.realm_access.roles.includes(role);
  }
  return false;
}

const userIsEmployee = computed(() =>{
  return userHasRole('dallmeier-employee');
});

const doPing = async () =>{
  const token_request = await fetch('/ping', {
    method: "POST",
    headers: {
      'Accept': 'application/json'
    }
  });

  if(!token_request.ok){
    throw new Error(`${token_request.statusText} (${token_request.status})`);
  }

  return await token_request.json();
}

const getLoginUri = async (callback = null) => {
  let url = '/login_link';
  if (callback != null) {
        const params = new URLSearchParams();
        params.append('redirect_to', callback);
        url += '?' + params.toString();
  }
  const dataResponse = await fetch(url,{
    method: "GET",
    cache: "no-cache"
  });

  if(!dataResponse.ok){
    throw new Error(`${dataResponse.statusText} (${dataResponse.status})`);
  }

  return dataResponse.text().then(login_uri => {
    return login_uri;
  });
}

const logout = () => {
  user.value.status = userStatus["2"];
  let refresh_data = {refresh_token: user.value.tokens.refresh_token};
  let logout_request = fetch('/logout', {
    method: "POST",
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(refresh_data)
  }).then(() => {
    localStorage.setItem('bearerToken', false);
    user.value.tokens.access_token = false;
    user.value.tokens.refresh_token = false;
    user.value.status = userStatus["0"];
    user.value.data = {};
    showMessage('Logout','Successfully logged out','success');
  }).catch(error => {
    showMessage('Logout','Something went wrong','danger');
  });
};

const ping = () => {
  doPing().then(tokens => {
    user.value.tokens.access_token = tokens.access_token;
    user.value.tokens.refresh_token = tokens.refresh_token;
    user.value.status = userStatus["1"];
    localStorage.setItem('bearerToken', getBearer());
    initUserData();
  }).catch(error => {
    console.error('ping error',error.status)
    user.value.tokens.access_token = false;
    user.value.status = userStatus["0"];
    user.value.data = {};
    localStorage.setItem('bearerToken', '');
    stopPing();
  });
};

const initUser = () => {
  user.value.status = userStatus["2"];
  if(USER_INIT.tokens.hasOwnProperty('access_token')){
    user.value.tokens = USER_INIT.tokens;
    user.value.status = userStatus["1"];
    localStorage.setItem('bearerToken', getBearer());
    startPing();
  }else{
    user.value.tokens.access_token = false;
    user.value.status = userStatus["0"];
  }
};

const initUserData = () => {
  if(!user.value.data.hasOwnProperty('azp')){
    getUserData().then(user_data => {
      user.value.data = user_data;
    })
  }
};

const startPing = () => {
  ping();
  pingRunner.value = setInterval(ping, 50000 );
};

const stopPing = () => {
  clearInterval(pingRunner.value);
};

defineExpose({getBearer,userLoggedIn,openLogin,userIsEmployee});

onMounted(()=>{
  initUser();
});

</script>

<template>
  <div class="row topbar position-fixed start-0 w-100 p-2 top-0 z-4 g-0">
    <div class="col">
      <slot></slot>
    </div>
    <div class="col">
      <button class="btn btn-auth" @click.prevent="openLogin(null)" v-if="userStatus['0'] === user.status">
        <i class="icon" :class="userIndicator"></i>
      </button>

      <div class="dropdown" v-if="userStatus['1'] === user.status">
        <button class="btn btn-auth dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
          <i class="icon" :class="userIndicator"></i> <span v-if="user.data.name">{{user.data.name}}</span>
        </button>
        <ul class="dropdown-menu">
          <li><a class="dropdown-item" href="#" @click.prevent="logout"><i class="icon logout"></i> Logout</a></li>
        </ul>
      </div>
    </div>
  </div>
</template>